import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createPermit,
  updatePermit,
  getPermit,
  // uploadPermitFile,
  // deletePermitAllFiles,
} from "../../api/permit";
import { v4 as uuid } from "uuid";
import TableCell from "../../components/common/ui/TableCell";
import AttachmentBox from "../../components/common/papers/AttachmentBox";
import ExcelUpload from "../../components/permit/ExcelUpload";

export default function PermitFormPage() {
  const navigate = useNavigate();
  const { id, permitId } = useParams();
  const isEdit = Boolean(permitId);

  const [name, setName] = useState("");
  const [permitType, setPermitType] = useState("");
  const [authority, setAuthority] = useState("");
  const [status, setStatus] = useState("planned");
  const [plannedSubmitDate, setPlannedSubmitDate] = useState("");
  const [submitDate, setSubmitDate] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [actualDate, setActualDate] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const attachmentRef = useRef(null);
  const [mode, setMode] = useState("single"); // 단건 or 일괄


  useEffect(() => {
    if (isEdit) {
      getPermit(permitId).then((data) => {
        setName(data.name || "");
        setPermitType(data.permit_type || "");
        setAuthority(data.authority || "");
        setStatus(data.status || "planned");
        setPlannedSubmitDate(data.planned_submit_date || "");
        setSubmitDate(data.submit_date || "");
        setPlannedDate(data.planned_date || "");
        setActualDate(data.actual_date || "");
        setDescription(data.description || "");
        setFiles((data.files || []).map((f) => ({ ...f, _id: uuid() })));
      });
    }
  }, [isEdit, permitId]);

  const handleSave = async () => {
    const payload = {
      project: id,
      name,
      permit_type: permitType,
      authority,
      status,
      planned_submit_date: plannedSubmitDate || null,
      submit_date: submitDate || null,
      planned_date: plannedDate || null,
      actual_date: actualDate || null,
      description,
    };

    try {
      let res;
      if (isEdit) {
        res = await updatePermit(permitId, payload);
        alert("인허가 정보가 수정되었습니다.");
      } else {
        res = await createPermit(payload);
        alert("인허가가 등록되었습니다.");
      }

      // ✅ 문서 DNS 추출 (백엔드에서 Docs 생성 시 dns 반환한다고 가정)
      const createdDns = res?.data?.dns || res?.dns || res?.data?.doc_dns;

      // ✅ 첨부 저장 (AttachmentBox 내부 로직 호출)
      if (createdDns && attachmentRef.current?.save) {
        console.log("📎 첨부 저장 시작:", createdDns);
        await attachmentRef.current.save(createdDns);
        console.log("📎 첨부 저장 완료");
      }

      navigate(`/project/${id}/permit/list`);
    } catch (err) {
      console.error(err);
      alert("저장 중 오류가 발생했습니다.");
    }
  };

  const handlePickFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    const newFiles = selected.map((file) => ({
      _id: uuid(),
      file,
      description: file.name,
      size: file.size,
      isNew: true,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    e.target.value = "";
  };

  return (
    <div className="p-4 sm:p-6">

      {/* 페이지 제목 */}
      <div className="mb-6">
        <h2 className="text-title">
          {isEdit ? "인허가 수정" : "인허가 등록"}
        </h2>
        <div className="h-px bg-gray-300 mt-2"></div>
      </div>

      {/* 탭 UI */}
      <div className="flex gap-3 mb-6 border-b">
        <button
          className={`px-4 py-2 text-sm font-medium ${mode === "single"
            ? "border-b-2 border-blue-600 text-blue-600"
            : "text-gray-500 hover:text-gray-700"
            }`}
          onClick={() => setMode("single")}
        >
          단건 등록
        </button>

        <button
          className={`px-4 py-2 text-sm font-medium ${mode === "bulk"
            ? "border-b-2 border-blue-600 text-blue-600"
            : "text-gray-500 hover:text-gray-700"
            }`}
          onClick={() => setMode("bulk")}
        >
          일괄 등록
        </button>
      </div>

      {mode === "single" && (
        <div className="bg-white p-6 border rounded-lg shadow-sm">


          {/* 테이블 */}
          <div className="overflow-x-auto flex justify-center ">
            <table className="border-collapse text-sm w-full ">
              <tbody>
                {/* 기본 정보 */}
                <tr >
                  <TableCell
                    label="인허가명"
                    editing
                    input={
                      <input
                        type="text"
                        className="input-base"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    }
                  />

                  <TableCell
                    label="종류"
                    editing
                    input={
                      <input
                        type="text"
                        className="input-base"
                        value={permitType}
                        onChange={(e) => setPermitType(e.target.value)}
                      />
                    }
                  />
                </tr>

                <tr  >
                  <TableCell
                    label="발급기관"
                    editing
                    input={
                      <input
                        type="text"
                        className="input-base"
                        value={authority}
                        onChange={(e) => setAuthority(e.target.value)}
                      />
                    }
                  />

                  <TableCell
                    label="상태"
                    editing
                    input={
                      <select
                        className="input-base"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="planned">계획</option>
                        <option value="in_progress">진행중</option>
                        <option value="completed">완료</option>
                        <option value="delayed">지연</option>
                      </select>
                    }
                  />
                </tr>

                {/* 날짜 */}
                <tr >
                  <TableCell
                    label="예정 접수일"
                    editing
                    input={
                      <input
                        type="date"
                        className="input-base"
                        value={plannedSubmitDate || ""}
                        onChange={(e) => setPlannedSubmitDate(e.target.value)}
                      />
                    }
                  />

                  <TableCell
                    label="실제 접수일"
                    editing
                    input={
                      <input
                        type="date"
                        className="input-base"
                        value={submitDate || ""}
                        onChange={(e) => setSubmitDate(e.target.value)}
                      />
                    }
                  />
                </tr>

                <tr  >
                  <TableCell
                    label="예정 완료일"
                    editing
                    input={
                      <input
                        type="date"
                        className="input-base"
                        value={plannedDate || ""}
                        onChange={(e) => setPlannedDate(e.target.value)}
                      />
                    }
                  />

                  <TableCell
                    label="실제 완료일"
                    editing
                    input={
                      <input
                        type="date"
                        className="input-base"
                        value={actualDate || ""}
                        onChange={(e) => setActualDate(e.target.value)}
                      />
                    }
                  />
                </tr>

                {/* 비고 */}
                <tr>
                  <TableCell
                    label="비고"
                    colSpan={3}
                    editing
                    input={
                      <textarea
                        className="input-base h-28 resize-none leading-relaxed"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    }
                  />
                </tr>
                {/* 첨부파일 */}
                <tr>
                  <TableCell
                    label="첨부자료"
                    colSpan={3}
                    editing
                    input={<AttachmentBox ref={attachmentRef} />}
                  />
                </tr>

              </tbody>
            </table>
          </div>
          {/* 등록/수정 버튼 */}
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={handleSave}
              className="btn-save"
            >
              {isEdit ? "수정" : "등록"}
            </button>
          </div>
        </div>
      )}
      {mode === "bulk" && (
        <div className="mt-4">
          <ExcelUpload onUploaded={() => navigate(`/project/${id}/permit/list`)} />
        </div>
      )}

    </div>

  );
}
